import { useFormContext, type ControllerRenderProps, type FieldPath } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/typeable_date_picker";
import { convertFormatStringToLocalTimezone, formatDateWithoutTimezoneToFormat } from "@/lib/api/date";
import { fieldGroupsFor, formatRecordFieldText, recordFieldLabels } from "../../recordFields";
import { recordFormFields, type RecordFormFieldName, type RecordFormValues } from "../recordFormSchema";
import { AccountCodeSelect } from "./AccountCodeSelect";
import { TraderSelect } from "./TraderSelect";

type TypeFieldPath = FieldPath<RecordFormValues> & `fields.${RecordFormFieldName}`;
type FieldProps = ControllerRenderProps<RecordFormValues, TypeFieldPath>;

const PORTAL_DATE_FORMAT = "YYYY-MM-DD";

function FieldInput({ name, field, disabled }: {
  name: RecordFormFieldName;
  field: FieldProps;
  disabled: boolean;
}) {
  const definition = recordFormFields[name];
  const value = field.value;

  switch (definition.input) {
    case "switch":
      return (
        <Switch
          checked={value === true}
          onCheckedChange={(checked: boolean) => {
            field.onChange(checked);
            field.onBlur();
          }}
          disabled={disabled}
        />
      );
    case "syncToRisk":
      // Ticked = synced to risk, i.e. skip_harvest_iq_sync is false.
      return (
        <Checkbox
          checked={value === false}
          onCheckedChange={(checked) => {
            field.onChange(checked !== true);
            field.onBlur();
          }}
          disabled={disabled}
        />
      );
    case "accountCode":
      return (
        <AccountCodeSelect
          value={Array.isArray(value) ? value : []}
          onChange={(codes: string[]) => {
            field.onChange(codes);
            field.onBlur();
          }}
          disabled={disabled}
        />
      );
    case "trader":
      return (
        <TraderSelect
          value={typeof value === "string" ? value : ""}
          onChange={(traderId: string) => {
            field.onChange(traderId);
            field.onBlur();
          }}
          disabled={disabled}
        />
      );
    case "percent":
      return (
        <div className="flex items-center gap-2">
          <Input
            name={field.name}
            ref={field.ref}
            inputMode="decimal"
            placeholder="e.g. 1.5"
            value={typeof value === "string" ? value : ""}
            onChange={field.onChange}
            onBlur={field.onBlur}
            disabled={disabled}
          />
          <span className="text-sm text-muted-foreground">%</span>
        </div>
      );
    case "select":
      return (
        <Combobox
          isModal
          dwimOnChange
          virtualizeList
          className="w-full"
          popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
          placeholder="—"
          options={(definition.options ?? []).map((option) => ({ value: option, label: formatRecordFieldText(name, option) }))}
          value={typeof value === "string" ? value : ""}
          onValueChange={(next: string) => {
            field.onChange(next);
            field.onBlur();
          }}
          disabled={disabled}
        />
      );
    case "date":
      return (
        <DatePicker
          containerClassName="w-full"
          value={typeof value === "string" && value ? convertFormatStringToLocalTimezone(value, PORTAL_DATE_FORMAT) : undefined}
          onChange={(date: Date | undefined) => {
            field.onChange(date ? formatDateWithoutTimezoneToFormat(date, PORTAL_DATE_FORMAT) : "");
            field.onBlur();
          }}
          disabled={disabled}
        />
      );
    case "textarea":
      return (
        <Textarea
          name={field.name}
          ref={field.ref}
          value={typeof value === "string" ? value : ""}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
        />
      );
    default:
      return (
        <Input
          name={field.name}
          ref={field.ref}
          value={typeof value === "string" ? value : ""}
          onChange={field.onChange}
          onBlur={field.onBlur}
          disabled={disabled}
        />
      );
  }
}

function TypeField({ name, disabled }: { name: RecordFormFieldName; disabled: boolean }) {
  const form = useFormContext<RecordFormValues>();
  const path: TypeFieldPath = `fields.${name}`;
  const input = recordFormFields[name].input;
  const isSwitch = input === "switch" || input === "syncToRisk";

  return (
    <FormField
      control={form.control}
      name={path}
      render={({ field }) => (
        <FormItem className={isSwitch ? "flex items-center justify-between gap-3 space-y-0 rounded-md border px-3 py-2" : undefined}>
          <FormLabel>{recordFieldLabels[name]}</FormLabel>
          <FormControl>
            <div data-testid={`crm-form-field-${name}`}>
              <FieldInput name={name} field={field} disabled={disabled} />
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

/** The record type's fields from GET /crm/rules, in the same groups as the detail page. */
export function TypeFieldsSection({ fields, disabled }: { fields: RecordFormFieldName[]; disabled: boolean }) {
  if (fields.length === 0) return null;
  const groups = fieldGroupsFor(fields, "form");

  return (
    <div className="space-y-6">
      {fields.includes("notes") && <TypeField name="notes" disabled={disabled} />}
      {groups.map((group) => (
        <section key={group.title} className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500">{group.title}</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {group.fields.map((field) => <TypeField key={field} name={field} disabled={disabled} />)}
          </div>
        </section>
      ))}
    </div>
  );
}
